import {
  AddOn,
  Appointment,
  CertificateResultOutcome,
  CertificateResultRow,
  MedicalCertificate,
  Service
} from '@/types';
import { getAppointmentServiceIds, isHealthSurveillanceName } from './appointmentServices';

// Drug & Alcohol rows (whether booked as a service or an add-on) are driven by
// the certificate's dedicated Drug Test Result picker, never by the generic
// fit/unfit dropdowns — a screening outcome is negative/non-negative, not a
// fitness verdict. "Not Recorded" stores 'not_recorded', so the booked test
// still prints as a line but with a blank result: the certificate must show
// only what the technician actually selected.
export const isDrugAndAlcoholName = (name: string): boolean => /drug|alcohol/i.test(name);

/** Placeholder for a booked D&A test with no technician-selected result. */
export const NO_DRUG_RESULT = 'not_recorded' as const;

interface AppointmentCatalogs {
  appointment: Appointment;
  servicesById: Map<string, Service>;
  addOnsById: Map<string, AddOn>;
}

interface BuildCertificateResultsInput extends AppointmentCatalogs {
  primaryOutcome: MedicalCertificate['outcome'];
  drugTestOutcome: MedicalCertificate['drugTestOutcome'];
  extraResults: Record<string, CertificateResultOutcome>;
}

interface PrintableItem {
  kind: 'service' | 'add_on';
  id: string;
  name: string;
}

const getPrintableItems = ({
  appointment,
  servicesById,
  addOnsById
}: AppointmentCatalogs): PrintableItem[] => {
  const services: PrintableItem[] = getAppointmentServiceIds(appointment)
    .map((id) => {
      const service = servicesById.get(id);
      return service ? ({ kind: 'service', id, name: service.name } as PrintableItem) : null;
    })
    .filter((item): item is PrintableItem => item !== null);
  const addOns: PrintableItem[] = (appointment.addOnIds || [])
    .map((id) => {
      const addOn = addOnsById.get(id);
      return addOn ? ({ kind: 'add_on', id, name: addOn.name } as PrintableItem) : null;
    })
    .filter((item): item is PrintableItem => item !== null);

  const items = [...services, ...addOns];
  const carriesFitness = (item: PrintableItem) =>
    !isHealthSurveillanceName(item.name) && !isDrugAndAlcoholName(item.name);
  return items.some(carriesFitness)
    ? items.filter((item) => !isHealthSurveillanceName(item.name))
    : items;
};

// One row per booked service and add-on test — persisted on the certificate so
// the template can print a result per line. The first row that carries a
// fitness verdict mirrors the clinical conclusion; the other non-D&A rows take
// their individually picked outcome (default fit).
export const buildCertificateResults = ({
  appointment,
  servicesById,
  addOnsById,
  primaryOutcome,
  drugTestOutcome,
  extraResults
}: BuildCertificateResultsInput): CertificateResultRow[] => {
  let primaryAssigned = false;
  return getPrintableItems({ appointment, servicesById, addOnsById }).map((item) => {
    if (isDrugAndAlcoholName(item.name)) {
      return {
        kind: item.kind,
        id: item.id,
        name: item.name,
        outcome: drugTestOutcome || NO_DRUG_RESULT
      };
    }
    const outcome = primaryAssigned
      ? extraResults[`${item.kind}:${item.id}`] || 'fit'
      : primaryOutcome || 'fit';
    primaryAssigned = true;
    return { kind: item.kind, id: item.id, name: item.name, outcome };
  });
};

export interface ExtraResultItem {
  key: string;
  name: string;
  kind: 'service' | 'add_on';
}

// The rows the technician sets individually. Excluded: the row that mirrors
// the clinical conclusion (the first one carrying a fitness verdict), every
// D&A service/add-on (mirrors the Drug Test Result picker), and the Health
// Surveillance shell (never printed alongside its own tests).
export const getExtraResultItems = ({
  appointment,
  servicesById,
  addOnsById
}: AppointmentCatalogs): ExtraResultItem[] => {
  let primarySeen = false;
  const items: ExtraResultItem[] = [];
  getPrintableItems({ appointment, servicesById, addOnsById }).forEach((item) => {
    if (isDrugAndAlcoholName(item.name)) return;
    if (!primarySeen) {
      primarySeen = true;
      return;
    }
    items.push({ key: `${item.kind}:${item.id}`, name: item.name, kind: item.kind });
  });
  return items;
};

export const hasDrugAndAlcoholBooked = ({
  appointment,
  servicesById,
  addOnsById
}: AppointmentCatalogs): boolean =>
  getAppointmentServiceIds(appointment).some((id) => {
    const service = servicesById.get(id);
    return Boolean(service && isDrugAndAlcoholName(service.name));
  }) ||
  (appointment.addOnIds || []).some((id) => {
    const addOn = addOnsById.get(id);
    return Boolean(addOn && isDrugAndAlcoholName(addOn.name));
  });

interface IssueCertificateWarningsInput {
  pendingTemplateNames: string[];
  submissionCount: number;
  drugAndAlcoholBooked: boolean;
  drugTestOutcome: MedicalCertificate['drugTestOutcome'];
}

// Pre-issue sanity checks surfaced to the technician in a single "Issue
// Anyway?" confirmation. None of them block issuing.
export const getIssueCertificateWarnings = ({
  pendingTemplateNames,
  submissionCount,
  drugAndAlcoholBooked,
  drugTestOutcome
}: IssueCertificateWarningsInput): string[] => {
  const warnings: string[] = [];
  if (pendingTemplateNames.length > 0) {
    warnings.push(
      `${pendingTemplateNames.length === 1 ? 'A questionnaire is' : `${pendingTemplateNames.length} questionnaires are`} still outstanding: ${pendingTemplateNames.join(', ')}.`
    );
  } else if (submissionCount === 0) {
    warnings.push('The health questionnaire has not been completed for this appointment.');
  }
  if (drugAndAlcoholBooked && !drugTestOutcome) {
    warnings.push(
      'A Drug & Alcohol test is booked, but the Drug Test Result is "Not Recorded" — no drug screening result will appear on the certificate.'
    );
  }
  return warnings;
};
